import { makeStyles } from 'tss-react/mui';

/**
 * useReportStyles - Premium themed report styles
 * Uses CSS variables for dynamic theming based on premiumTokens
 */
export default makeStyles()((theme) => {
  const isDark = theme.palette.mode === 'dark';

  // Premium tokens inline (para makeStyles que não pode usar hooks)
  const tokens = {
    surface: {
      base: isDark ? '#0d1117' : '#ffffff',
      elevated: isDark ? '#161b22' : '#f6f8fa',
    },
    border: {
      subtle: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)',
      default: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    },
    text: {
      primary: isDark ? '#e6edf3' : '#1f2328',
      secondary: isDark ? '#8b949e' : '#656d76',
    },
    accent: {
      primary: isDark ? '#00d4aa' : '#00a884',
      subtle: isDark ? 'rgba(0, 212, 170, 0.1)' : 'rgba(0, 168, 132, 0.08)',
    },
    crud: {
      table: {
        headerBg: isDark ? '#161b22' : '#f6f8fa',
        headerText: isDark ? '#8b949e' : '#656d76',
        rowHover: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
        rowSelected: isDark ? 'rgba(0, 212, 170, 0.08)' : 'rgba(0, 168, 132, 0.06)',
        borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
      },
    },
  };

  return {
    container: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    containerMap: {
      flexBasis: '40%',
      flexShrink: 0,
    },
    containerMain: {
      overflow: 'auto',
      // Premium table styling
      '& .MuiTable-root': {
        '& .MuiTableHead-root': {
          backgroundColor: tokens.crud.table.headerBg,
          '& .MuiTableCell-head': {
            color: tokens.crud.table.headerText,
            fontWeight: 600,
            fontSize: '0.8125rem',
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            borderBottom: `1px solid ${tokens.crud.table.borderColor}`,
            padding: theme.spacing(1.5, 2),
          },
        },
        '& .MuiTableBody-root': {
          '& .MuiTableRow-root': {
            transition: 'background-color 150ms ease',
            '&:hover': {
              backgroundColor: tokens.crud.table.rowHover,
            },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: tokens.crud.table.rowSelected,
            },
          },
          '& .MuiTableCell-body': {
            color: tokens.text.primary,
            fontSize: '0.875rem',
            borderBottom: `1px solid ${tokens.crud.table.borderColor}`,
            padding: theme.spacing(1.25, 2),
          },
        },
      },
    },
    header: {
      position: 'sticky',
      left: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      backgroundColor: tokens.surface.base,
      borderBottom: `1px solid ${tokens.border.subtle}`,
    },
    columnAction: {
      width: '1%',
      paddingLeft: theme.spacing(1),
      '@media print': {
        display: 'none',
      },
    },
    columnActionContainer: {
      display: 'flex',
    },
    filter: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'stretch',
      gap: theme.spacing(1.5),
      padding: theme.spacing(2, 1.5),
      '@media print': {
        display: 'none !important',
      },
    },
    // Versão compacta do filter para sidebars estreitas (ReplayPage, etc)
    filterCompact: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      padding: 0,
      '@media print': {
        display: 'none !important',
      },
      // Override com !important — filterItem define flex: '1 1 150px' com mesma
      // especificidade e vence por ordem no stylesheet, inflando os items verticalmente
      '& > *': {
        flex: '0 0 auto !important',
        minWidth: 'unset !important',
        maxWidth: 'none !important',
        width: '100%',
      },
    },
    filterItem: {
      flex: '1 1 150px',
      minWidth: 120,
      maxWidth: 260,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    },
    // Campos médios (ex: Período, selects curtos)
    filterItemMedium: {
      flex: '1 1 120px',
      minWidth: 110,
      maxWidth: 170,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    },
    // Campos datetime - crescem moderadamente
    filterItemDateTime: {
      flex: '1 1 160px',
      minWidth: 155,
      maxWidth: 195,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    },
    // Campo numérico pequeno (ex: preço combustível)
    filterItemSmall: {
      flex: '1 1 120px',
      minWidth: 115,
      maxWidth: 155,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
    },
    // Botão de ação - cresce mas com limite, alinhado com os campos
    filterItemButton: {
      flex: '1 1 140px',
      minWidth: 120,
      maxWidth: 200,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      '& .MuiButton-root, & .MuiButtonBase-root': {
        minHeight: 40,
      },
    },
    filterButtons: {
      display: 'flex',
      gap: theme.spacing(1),
      flex: `1 1 ${theme.dimensions.filterFormWidth}`,
    },
    filterButton: {
      flexGrow: 1,
    },
    chart: {
      flexGrow: 1,
      overflow: 'hidden',
    },
    actionCellPadding: {
      '&.MuiTableCell-body': {
        paddingTop: 0,
        paddingBottom: 0,
      },
      '@media print': {
        display: 'none',
      },
    },
    // Premium Stats Bar
    statsBar: {
      display: 'flex',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
      padding: theme.spacing(1, 2, 1.5),
      borderTop: `1px solid ${tokens.border.subtle}`,
      backgroundColor: tokens.surface.elevated,
    },
    // Premium Chip styles
    statChip: {
      borderRadius: '6px',
      fontWeight: 500,
      fontSize: '0.8125rem',
      '& .MuiChip-icon': {
        color: 'inherit',
      },
    },
  };
});
